import { type Client, createClient, createConfig } from '@zealot/shared/apiClient/client';
import { createContext, type ReactNode, useContext, useEffect, useMemo, useRef } from 'react';
import { type BlockedState, useBannedUser } from '@/contexts/BannedUserContext';
import { BRAND_CHOOSER_PATH } from '@/lib/appBootstrap/signedInLanding';
import { createAuthRetryInterceptor } from '@/lib/auth/authRetryInterceptor';
import { useAuth } from '@/pages/AuthContext';
import { hubBrandUuidForRequest, useAppStore } from '@/store';
import { NEW_API_URL } from '@/utils/api';

type ApiClientContextValue = {
  client: Client;
};

const ApiClientContext = createContext<ApiClientContextValue | null>(null);

type ApiClientProviderProps = {
  children: ReactNode;
};

// gloss
const MEMBERSHIP_REQUIRED_CODE = 'BRAND_MEMBERSHIP_REQUIRED';

// gloss
const STATE_BY_CODE: Record<string, BlockedState> = {
  USER_BANNED: 'banned',
  SIGNUP_UNAVAILABLE: 'unavailable',
  SIGNUP_PENDING_REVIEW: 'pending',
  APPLICATION_REJECTED: 'rejected',
  APPLICATION_REQUIRED: 'applicationRequired',
};

// gloss
export const ApiClientProvider = ({ children }: ApiClientProviderProps) => {
  const { fetchToken } = useAuth();
  const { setBlocked } = useBannedUser();

  const fetchTokenRef = useRef(fetchToken);
  useEffect(() => {
    fetchTokenRef.current = fetchToken;
  }, [fetchToken]);

  const isLeavingRefusedHubRef = useRef(false);

  const setBlockedRef = useRef(setBlocked);
  useEffect(() => {
    setBlockedRef.current = setBlocked;
  }, [setBlocked]);

  const client = useMemo(() => {
    // why: `@/utils/config` throws on an unknown NEXT_PUBLIC_ENVIRONMENT, so in any real
    // why: build this fallback is unreachable — that guard, not this line, is what stops a
    // why: deploy from silently talking to localhost. It stays for vitest, which runs with
    // why: no deployment environment; removing it made the client build relative URLs and
    // why: broke unrelated portal tests. See ZLT-4885.
    const baseUrl = NEW_API_URL || 'http://localhost:8000';
    const apiClient = createClient(createConfig({ baseUrl }));

    apiClient.interceptors.request.use(async (request) => {
      const token = await fetchTokenRef.current();
      if (token) {
        request.headers.set('Authorization', `Bearer ${token}`);
      }
      const resolvedBrandUuid = hubBrandUuidForRequest();
      if (resolvedBrandUuid) {
        request.headers.set('x-brand-uuid', resolvedBrandUuid);
      }
      return request;
    });

    apiClient.interceptors.response.use(async (response) => {
      if (response.status >= 400) {
        const body = await response
          .clone()
          .json()
          .catch(() => null as unknown);
        const code = (body as { code?: string } | null)?.code;
        if (code === MEMBERSHIP_REQUIRED_CODE) {
          const refusedUuid = (body as { details?: { brandUuid?: unknown } } | null)?.details?.brandUuid;
          useAppStore.getState().hubBrand.refuse(typeof refusedUuid === 'string' ? refusedUuid : undefined);
          useAppStore.getState().brandTheme.clear();
          if (!isLeavingRefusedHubRef.current && window.location.pathname !== BRAND_CHOOSER_PATH) {
            isLeavingRefusedHubRef.current = true;
            window.location.assign(BRAND_CHOOSER_PATH);
          }
          return response;
        }
        if (code && STATE_BY_CODE[code]) {
          const message = (body as { message?: string }).message;
          const supportEmail = (body as { details?: { supportEmail?: string } } | null)?.details?.supportEmail;
          setBlockedRef.current(
            STATE_BY_CODE[code]!,
            typeof message === 'string' ? message : undefined,
            typeof supportEmail === 'string' ? supportEmail : undefined,
          );
        }
      }
      return response;
    });

    apiClient.interceptors.response.use(createAuthRetryInterceptor(fetchTokenRef));

    return apiClient;
  }, []);

  const value = useMemo(() => ({ client }), [client]);

  return <ApiClientContext.Provider value={value}>{children}</ApiClientContext.Provider>;
};

export const useApiClient = (): Client => {
  const ctx = useContext(ApiClientContext);
  if (!ctx) {
    throw new Error('useApiClient must be used within ApiClientProvider');
  }
  return ctx.client;
};
